import { Trash2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useAuth } from '@/features/auth/store'
import { cartSubtotal, cartTax, useCart } from '@/features/marketplace/cart'
import { quoteShipping, useStorefrontSettings } from '@/features/marketplace/useStorefront'
import { ProductImage } from '@/routes/storefront/ProductImage'
import { QuantityStepper } from '@/routes/storefront/QuantityStepper'

/**
 * The basket (M24-03).
 *
 * Every figure on this screen is an **estimate**, and says so. The basket lives in
 * `localStorage` with a price snapshotted whenever the product was added; the binding
 * amounts -- subtotal, discount, tax, delivery, `amount_due` -- are computed by the server
 * when the order is placed, by the same code that prices a rep's order. Showing an
 * unqualified total here would be promising a number this screen is not entitled to
 * promise, and a basket that has sat in a browser for a week is exactly the case where it
 * would be wrong.
 *
 * Signing in is not required to reach this page. The account wall is at checkout and
 * nowhere earlier: asking a stranger to register before they can see what their basket
 * costs is how a shop loses them.
 */
export function StorefrontCart() {
  const lines = useCart((s) => s.lines)
  const setQuantity = useCart((s) => s.setQuantity)
  const remove = useCart((s) => s.remove)
  const clear = useCart((s) => s.clear)
  const status = useAuth((s) => s.status)
  const navigate = useNavigate()
  const { settings, money, isLoading: settingsLoading } = useStorefrontSettings()

  const goods = cartSubtotal(lines)
  const tax = cartTax(lines)
  const shipping = quoteShipping(settings, goods + tax)

  if (lines.length === 0) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Your basket
        </h1>
        <Card>
          <CardBody className="space-y-4 py-12 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">Your basket is empty.</p>
            <Link to="/store" className="inline-block">
              <Button>Browse the shop</Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Your basket
        </h1>
        <Button variant="ghost" size="sm" onClick={clear}>
          Empty basket
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          {lines.map((line) => (
            <Card key={line.product_id}>
              <CardBody className="flex gap-4 p-4">
                <Link
                  to={`/store/${line.product_id}`}
                  className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-slate-100 dark:bg-slate-800"
                >
                  <ProductImage src={line.image} name={line.name} textClass="text-sm" />
                </Link>

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link
                        to={`/store/${line.product_id}`}
                        className="block truncate text-sm font-medium text-slate-900 hover:underline dark:text-slate-100"
                      >
                        {line.name}
                      </Link>
                      <p className="text-xs text-slate-400">
                        {money(line.price)} per {line.unit_of_measure}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(line.product_id)}
                      aria-label={`Remove ${line.name}`}
                      className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-danger dark:hover:bg-slate-800"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <QuantityStepper
                      value={line.quantity}
                      onChange={(quantity) => setQuantity(line.product_id, quantity)}
                      label={`Quantity of ${line.name}`}
                    />
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {money(Number(line.price) * line.quantity)}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>

        <div className="space-y-3">
          <Card>
            <CardHeader title="Estimate" />
            <CardBody className="space-y-2 text-sm">
              <Row label="Goods" value={money(goods)} />
              <Row label="Tax" value={money(tax)} />
              <Row
                label="Delivery"
                // "Free" is a claim, and until the shop's terms have arrived it is one this
                // screen cannot make: an unloaded `settings` quotes zero the same way a
                // genuinely free delivery does. An em dash for the half-second in between.
                value={
                  settingsLoading ? '—' : shipping.amount > 0 ? money(shipping.amount) : 'Free'
                }
              />
              <div className="flex items-baseline justify-between border-t border-slate-200 pt-2 dark:border-slate-800">
                <span className="font-medium text-slate-900 dark:text-slate-100">Estimated</span>
                <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {money(goods + tax + shipping.amount)}
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Confirmed at checkout, where any discount on your account is applied.
              </p>
            </CardBody>
          </Card>

          {shipping.shortfall != null && shipping.shortfall > 0 && (
            <Alert tone="info">Spend {money(shipping.shortfall)} more for free delivery.</Alert>
          )}

          <Button
            className="w-full"
            onClick={() =>
              // The account wall, and the only one. Somewhere to come back to is passed
              // along so signing in does not lose the basket's place.
              navigate(status === 'authenticated' ? '/store/checkout' : '/store/login', {
                state: { from: '/store/checkout' },
              })
            }
          >
            {status === 'authenticated' ? 'Checkout' : 'Sign in to check out'}
          </Button>

          <Link
            to="/store"
            className="block text-center text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            Continue shopping
          </Link>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-slate-900 dark:text-slate-100">{value}</span>
    </div>
  )
}
